import React, { useState, useEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { styled } from '@mui/material/styles';

type TProps = {
  src: string;
  resizeFlg?: boolean; // 画面リサイズ有無
};

const options = {
  cMapUrl: '/cmaps/'
};

// workerの設定
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

export const PDFViewer: React.FC<TProps> = ({ src, resizeFlg = true }) => {
  const isMobile = () => /Mobi|Android|iPhone/i.test(navigator.userAgent);
  const isTablet = () =>
    /iPad|Android/i.test(navigator.userAgent) ||
    (/Macintosh/i.test(navigator.userAgent) && document.ontouchstart === null);
  const isPC = !isMobile() && !isTablet();

  const [totalPages, setTotalPages] = useState<number>(0);
  const [pageScale, setPageScale] = useState<number>(calculatePageScale());

  function calculatePageScale() {
    const width = window.innerWidth;
    if (width < 500) return 0.3; // モバイル用
    if (width < 1000) return 0.6; // タブレット用
    return 1; // PC用
  }

  useEffect(() => {
    if (!resizeFlg || isPC) {
      return;
    }

    let currentWidth = window.innerWidth;

    const handleResize = () => {
      if (currentWidth != window.innerWidth) {
        currentWidth = window.innerWidth;
        setPageScale(calculatePageScale()); // スケールだけを再計算
      }
    };

    const handleOrientationChange = () => {
      setPageScale(calculatePageScale()); // 画面回転時にスケール調整
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleOrientationChange);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleOrientationChange);
    };
  }, [resizeFlg, isPC]);

  if (isPC) {
    // デスクトップ用の表示（iframe
    return <StyledIframe src={src} />;
  }

  // モバイルやタブレット用の表示（react-pdf）
  const onLoadSuccess = ({ numPages }: { numPages: number }) => {
    setTotalPages(numPages);
  };

  const pages = Array.from({ length: totalPages }, (_, i) => (
    <StyledPage pageIndex={i} pageScale={pageScale} key={i} />
  ));

  return (
    <Viewer>
      <Document file={src} onLoadSuccess={onLoadSuccess} options={options}>
        {pages}
      </Document>
    </Viewer>
  );
};

const StyledIframe: React.FC<{ src: string }> = ({ src }) => (
  <iframe
    src={src}
    style={{ width: '100%', height: '80vh', borderStyle: 'none' }}
  />
);

const StyledPage: React.FC<{ pageIndex: number; pageScale: number }> = ({
  pageIndex,
  pageScale
}) => {
  return (
    <div style={{ marginBottom: '10px' }}>
      <Page
        key={pageIndex}
        pageNumber={pageIndex + 1}
        scale={pageScale}
        renderTextLayer={false}
        renderAnnotationLayer={false}
      />
    </div>
  );
};

const Viewer = styled('div')({
  width: '100%',
  height: '100%',
  overflow: 'scroll',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  alignSelf: 'center'
});
